/**
 * Agent portal — /agent/commissions.
 *
 * Sole page in the agent surface. Date-range picker + accrued totals
 * + per-reseller breakdown. Calls /v1/agent/commissions which the
 * backend gates with RequireAgent; the frontend mirror gate is in
 * RequireAgentRoute (router.tsx).
 *
 * Default range: current calendar month from day 1 → today. Operator
 * can pick anything — backend doesn't bound the range, but a wide
 * scan against messages is server-side cost so we lean on the UI to
 * default to a sane window.
 */

import { type JSX, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, Skeleton } from '@/components/ui';
import { useMyCommissions, type AgentAccruedItem } from '@/hooks/useMyCommissions';
import { ApiError } from '@/lib/api';
import { formatCurrency } from '@/lib/format';

function startOfThisMonthISO(): string {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1, 0, 0, 0)).toISOString();
}
function endOfTodayISO(): string {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59)).toISOString();
}

// HTML <input type="date"> wants YYYY-MM-DD; the API wants RFC3339.
// These two helpers convert between the formats so the date picker
// stays simple and the query params are well-formed.
function isoToDateInput(iso: string): string {
  const d = new Date(iso);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
function dateInputToISOStart(s: string): string {
  // Treat the picker's date as UTC midnight start.
  return new Date(`${s}T00:00:00Z`).toISOString();
}
function dateInputToISOEnd(s: string): string {
  return new Date(`${s}T23:59:59Z`).toISOString();
}

export default function AgentCommissionsPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);

  const [fromDate, setFromDate] = useState(() => isoToDateInput(startOfThisMonthISO()));
  const [toDate, setToDate] = useState(() => isoToDateInput(endOfTodayISO()));

  const fromISO = useMemo(() => dateInputToISOStart(fromDate), [fromDate]);
  const toISO = useMemo(() => dateInputToISOEnd(toDate), [toDate]);

  const q = useMyCommissions(fromISO, toISO);
  const data = q.data;

  return (
    <div className="px-8 py-8 max-w-[1100px] mx-auto">
      <header className="mb-6">
        <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-none m-0">
          {t('agc_title')}
        </h1>
        <p className="text-[13px] text-text-3 mt-1.5">{t('agc_sub')}</p>
      </header>

      <Card padding="md" className="mb-5">
        <div className="flex items-end gap-3 flex-wrap">
          <label className="block">
            <span className="text-[12px] font-medium text-text-2 mb-1.5 block">
              {t('agc_from')}
            </span>
            <input
              type="date"
              value={fromDate}
              max={toDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-10 px-3 text-[13px] bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500"
            />
          </label>
          <label className="block">
            <span className="text-[12px] font-medium text-text-2 mb-1.5 block">
              {t('agc_to')}
            </span>
            <input
              type="date"
              value={toDate}
              min={fromDate}
              onChange={(e) => setToDate(e.target.value)}
              className="h-10 px-3 text-[13px] bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500"
            />
          </label>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card padding="lg" className="lg:col-span-1">
          <div className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em] mb-2">
            {t('agc_total_accrued')}
          </div>
          {q.isLoading ? (
            <Skeleton className="h-10 w-32" />
          ) : (
            <div className="num display text-[34px] font-semibold text-text tracking-tight leading-none">
              {formatCurrency(data?.total_accrued ?? 0, lang)}
            </div>
          )}
          <p className="text-[12px] text-text-3 mt-3 leading-snug">{t('agc_total_hint')}</p>
        </Card>

        <Card padding="lg" className="lg:col-span-2">
          <div className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em] mb-2">
            {t('agc_period_label')}
          </div>
          <div className="text-[14px] text-text-2">
            {t('agc_period_value', {
              from: new Date(fromISO).toLocaleDateString(lang),
              to: new Date(toISO).toLocaleDateString(lang),
            })}
          </div>
          <p className="text-[12px] text-text-3 mt-3 leading-snug">{t('agc_period_hint')}</p>
        </Card>
      </div>

      {q.error && (
        <Card padding="md" className="mb-4">
          <p className="text-[13px] text-danger">
            {q.error instanceof ApiError ? q.error.message : t('agc_err_generic')}
          </p>
        </Card>
      )}

      <Card padding="none">
        <div className="px-5 py-4 border-b border-line">
          <h2 className="display text-[16px] font-semibold text-text">
            {t('agc_breakdown_title')}
          </h2>
          <p className="text-[12px] text-text-3 mt-0.5">{t('agc_breakdown_sub')}</p>
        </div>
        {q.isLoading ? (
          <div className="p-5 space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (data?.items.length ?? 0) === 0 ? (
          <div className="p-10 text-center text-[13px] text-text-3">{t('agc_empty')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-5 py-2.5 font-medium">{t('agc_col_reseller')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('agc_col_basis')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('agc_col_pct')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('agc_col_base_amount')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('agc_col_accrued')}</th>
                </tr>
              </thead>
              <tbody>
                {(data?.items ?? []).map((row) => (
                  <CommissionRow key={row.commission_id} row={row} lang={lang} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

interface CommissionRowProps {
  row: AgentAccruedItem;
  lang: string;
}
function CommissionRow({ row, lang }: CommissionRowProps): JSX.Element {
  const { t } = useTranslation('default');
  const baseAmount = row.basis === 'gross_revenue' ? row.gross_revenue : row.net_margin;
  return (
    <tr className="border-b border-line last:border-0 hover:bg-surface-2">
      <td className="px-5 py-3 text-text font-medium">{row.reseller_name}</td>
      <td className="px-5 py-3 text-text-2">
        {row.basis === 'gross_revenue'
          ? t('aac_basis_gross')
          : t('aac_basis_net')}
      </td>
      <td className="px-5 py-3 num text-right">{row.percentage.toFixed(1)}%</td>
      <td className="px-5 py-3 num text-right text-text-2">
        {formatCurrency(baseAmount, lang)}
      </td>
      <td className="px-5 py-3 num text-right text-text font-semibold">
        {formatCurrency(row.accrued, lang)}
      </td>
    </tr>
  );
}
